import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import { DatoCmsComparisonProduct, DatoCmsMasterCoverage, DatoCmsProductComparisonTable, DatoCmsProductCoverage } from '../../../schema/datocms';
import { Themes } from '../../../schema/enum/themes';
import { getColumnSpanClass, getColumnStartClass, getTextAlignmentClass } from '../../../utilities';
import HtmlEntityTextComponent from '../../html-entity-text/html-entity-text-component';
import HtmlStringComponent from '../../html-string/html-string-component';
import Icon from '../../icon/icon';
import Link from '../../link/link';
import { useThemeClassMap } from '../../theme/useThemeClassMap';

const MasterCoverageStaticQuery = graphql`
    query MasterCoverageStaticQuery {
        masterCoverages: allDatoCmsMasterCoverage(sort: { position: ASC }) {
            nodes {
                ...MasterCoverageFragment
            }
        }
    }
`;

interface MasterCoverageStaticQueryResult {
    masterCoverages: { nodes: DatoCmsMasterCoverage[] };
}

interface CoverageDisplayData {
    expanded: boolean;
    name: string;
    masterId: string;
    description: string;
    limitDescriptionByProductId: Map<string, string>;
}

const ProductComparisonTableComponent = (
    {
        columnLayout, title, titleAlignment, subtitle, subtitleAlignment, descriptionHtml, products, bottomContentHtml
    }: DatoCmsProductComparisonTable
): React.ReactElement => {
    const { masterCoverages }: MasterCoverageStaticQueryResult = useStaticQuery(MasterCoverageStaticQuery);

    const initialDisplayCoverages: CoverageDisplayData[] = masterCoverages.nodes.filter((mc) => mc.showCoverageOnPct).map((mc) => {
        const limitDescriptionByProductId: Map<string, string> = new Map<string, string>();
        products.forEach((p) => {
            const currentCoverage: DatoCmsProductCoverage | undefined = p.product.coverages.find(c => c.coverage.masterRatingId === mc.masterRatingId);
            limitDescriptionByProductId.set(p.product.ratingId, currentCoverage?.limit || '-');
        });

        return {
            expanded: false,
            name: mc.name,
            masterId: mc.masterRatingId,
            description: mc.description,
            limitDescriptionByProductId
        };
    });

    const [displayCoverages, setDisplayCoverages] = useState(initialDisplayCoverages);
    const [mobileSelectedProduct, setMobileSelectedProduct] = useState<DatoCmsComparisonProduct | undefined>(undefined);

    function toggleCoverage(coverage: CoverageDisplayData): void {
        const updatedCoverages: CoverageDisplayData[] = [...displayCoverages];
        updatedCoverages[displayCoverages.findIndex(c => c.masterId === coverage.masterId)] = {
            ...coverage,
            expanded: !coverage.expanded
        };

        setDisplayCoverages(updatedCoverages);
    }

    const themeClass = useThemeClassMap(Themes.WHITE);

    const gridColumnSpanClass: string = getColumnSpanClass(columnLayout);
    const gridColumnStartClass: string = getColumnStartClass(columnLayout);

    return (
        <div className={`${ themeClass } page-component`}>
            <div className="container mx-auto lg:grid lg:grid-cols-12">
                <div className={`lg:${gridColumnSpanClass} lg:${gridColumnStartClass}`}>
                    { title && <h2 className={`text-center md:${getTextAlignmentClass(titleAlignment)}`}>
                        { <HtmlEntityTextComponent text={title} /> }
                    </h2> }

                    { subtitle && <p className={`pt-4 text-theme-primary-text text-xl ${getTextAlignmentClass(subtitleAlignment)}`}>{subtitleAlignment}</p> }

                    { descriptionHtml && <HtmlStringComponent className="pt-4" htmlString={descriptionHtml} />}

                    <div className="hidden lg:flex justify-center py-8">
                        <table className="table-fixed">
                            { title && <caption className="hidden">{title}</caption> }
                            <thead>
                                <tr className="space-x-24 align-top">
                                    <td></td>
                                    {
                                        products.map((product, index) => (
                                            <th key={index} scope="col" className={`w-1/${products.length + 1}`}>
                                                <div className="flex flex-col items-center space-y-2">
                                                    <Link className="flex-shrink-0 flex flex-col items-center space-y-2"
                                                        internalPageReference={product.productLink[0].internalPageReference}
                                                        internalPageSectionId={product.productLink[0].internalSectionComponentId}
                                                        externalPageUrl={product.productLink[0].externalPageUrl}
                                                        title={product.productLink[0].descriptiveTitle}>
                                                        <img src={product.product.linkLogoImage.url} alt={product.product.linkLogoImage.alt} style={{ width: '7rem'}} />
                                                        <h3 className="text-theme-secondary">
                                                            { <HtmlEntityTextComponent text={product.product.name} /> || product.product.name }
                                                        </h3>
                                                    </Link>
                                                    <span className="uppercase font-normal">
                                                        { <HtmlEntityTextComponent text={product.product.description} /> || product.product.description }
                                                    </span>
                                                </div>
                                            </th>))
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="bg-brand-light-gray font-bold py-2 px-4 text-2xl text-theme-primary-text" colSpan={products.length + 1}>Coverages</td>
                                </tr>
                                {
                                    displayCoverages.map((mc, index) => {
                                        return (
                                            <React.Fragment key={index}>
                                                <tr className={mc.expanded ? '' : 'border-b bottom-1'}>
                                                    <th scope="row" className={`text-left pt-8 ${mc.expanded ? 'pb-6' : 'pb-8'} px-4`}><button className="text-left text-xl font-bold text-theme-secondary" aria-label="Click to view coverage description" onClick={e => toggleCoverage(mc)}>{mc.name} <span className="text-sm">▼</span></button></th>
                                                    {products.map((product, productIndex) => <td key={productIndex} className="text-center">{mc.limitDescriptionByProductId.get(product.product.ratingId)}</td>)}
                                                </tr>
                                                <tr className={mc.expanded ? 'border-b bottom-1' : 'hidden'}>
                                                    <td className="bg-primary text-white py-6 px-4" colSpan={products.length + 1}>{mc.description}</td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col space-y-8 py-8 lg:hidden border-t border-1">
                        {
                            products.map((product, index) => (
                                <div key={index} className="flex flex-col items-center py-4 space-y-2 border-b border-1">
                                    <Link className="flex-shrink-0 flex flex-col items-center space-y-2"
                                        internalPageReference={product.productLink[0].internalPageReference}
                                        internalPageSectionId={product.productLink[0].internalSectionComponentId}
                                        externalPageUrl={product.productLink[0].externalPageUrl}
                                        title={product.productLink[0].descriptiveTitle}>
                                        <img src={product.product.linkLogoImage.url} alt={product.product.linkLogoImage.alt} style={{ width: '8rem'}} />
                                        <h3 className="text-theme-secondary">
                                            { <HtmlEntityTextComponent text={product.product.name} /> || product.product.name }
                                        </h3>
                                    </Link>
                                    <span className="uppercase font-normal text-center">
                                        { <HtmlEntityTextComponent text={product.product.description} /> || product.product.description }
                                    </span>

                                    <button className="btn bordered" onClick={() => setMobileSelectedProduct(product)}>View Coverages</button>
                                </div>))
                        }
                    </div>
                </div>
            </div>

            {
                mobileSelectedProduct &&
                <div className="fixed inset-0 overflow-scroll bg-white h-full w-full lg:hidden">
                    <div className="bg-brand-light-gray font-bold py-2 px-4 text-theme-primary-text flex justify-between items-center">
                        <span className="text-2xl">Coverages</span>
                        <Icon name="close" className="w-12 h-12 text-primary fill-current" onClick={() => setMobileSelectedProduct(undefined)} />
                    </div>
                    {
                        displayCoverages.map((mc, index) => {
                            const coverageName =mc.limitDescriptionByProductId.get(mobileSelectedProduct.product.ratingId);
                            return (
                                <React.Fragment key={index}>
                                    <div className="border-b bottom-1">
                                        <div className={`pt-8 pb-${mc.expanded ? 6 : 8} px-4 flex flex-col items-start`}>
                                            <button className="text-left text-xl font-bold text-theme-secondary" aria-label="Click to view coverage description" onClick={e => toggleCoverage(mc)}>{mc.name} <span className="text-sm">▼</span>
                                            </button>
                                            <div className="text-left">
                                                { <HtmlEntityTextComponent text={coverageName ? coverageName: ''} /> || coverageName }
                                            </div>
                                        </div>

                                        { mc.expanded &&
                                            <div>
                                                <div className="bg-primary text-white py-6 px-4">
                                                    { <HtmlEntityTextComponent text={mc.description} /> || mc.description }
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </React.Fragment>
                            );
                        })
                    }
                </div>
            }

            { bottomContentHtml && <HtmlStringComponent htmlString={bottomContentHtml} /> }
        </div>
    );
};

export default ProductComparisonTableComponent;
